import React, { useState, useEffect } from "react";
import { Button } from "@relume_io/relume-ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@relume_io/relume-ui";
import { AnimatePresence, motion } from "framer-motion";

const HomeBanner = ({autoSwitchInterval = 10000 }) => {
    const [activeTab, setActiveTab] = useState();
    const [tabs, setTabs] = useState({ trigger: [], content: [] });

    // Fetch carousel items from API
    useEffect(() => {
        const fetchCarouselItems = async () => {
            try {
                const response = await fetch("/api/homepagecarousel/GetCarouselItemsPublic");
                const data = await response.json();

                // get tabs
                const tabs = data.map((item, index) => ({
                    value: `tab-${index + 1}`,
                    text: item.tabName,
                }));
                
                // set content tabs
                const content = data.map((item, index) => ({
                    value: `tab-${index + 1}`,
                    heading: item.title,
                    description: item.subtitle,
                    buttons: item.buttonLink
                        ? [{ title: "Learn More", href: item.buttonLink }]
                        : [],
                    image: {
                        src: `/fsc-images/homepage-carousel/${item.imageUrl}`,
                        alt: item.title,
                    },
                }));

                setTabs({ trigger: tabs, content });
                setActiveTab("tab-1")
            } catch (error) {
                console.error("Failed to fetch carousel items:", error);
            }
        };

        fetchCarouselItems();
    }, []);

    // Automatically switch tabs after a specified interval
    useEffect(() => {
        if (!tabs.trigger.length) return;

        const interval = setInterval(() => {
            setActiveTab((prev) => {
                const currentIndex = tabs.trigger.findIndex((tab) => tab.value === prev);
                const nextIndex = (currentIndex + 1) % tabs.trigger.length;
                return tabs.trigger[nextIndex].value;
            });
        }, autoSwitchInterval);

        return () => clearInterval(interval);
    }, [tabs.trigger, autoSwitchInterval]);

    if (!tabs.trigger.length)
        return (
            <motion.div
                className="flex h-[80vh] w-full items-center justify-center bg-gray-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
            />
        );
    return (
        <div className="relative min-h-[80vh]">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <AnimatePresence initial={false}>
                    {tabs.content.map(
                        (content, index) =>
                            content.value === activeTab && (
                                <TabsContent
                                    key={index}
                                    value={content.value}
                                    className="relative max-h-[40rem] min-h-[80vh] overflow-visible"
                                >
                                    <TabContent {...content} />
                                </TabsContent>
                            )
                    )}
                </AnimatePresence>
                <TabsList
                    className="absolute bottom-12 left-0 right-0 top-auto z-20 mx-auto flex justify-center gap-2 px-2 sm:gap-4 sm:px-[5vw] md:bottom-16 lg:bottom-20 lg:max-w-xl"
                >
                    {tabs.trigger.map((trigger, index) => (
                        <TabsTrigger
                            key={index}
                            value={trigger.value}
                            onClick={() => setActiveTab(trigger.value)}
                            className="relative flex-1 min-w-0 whitespace-normal border-0 bg-transparent px-2 py-2 text-center text-sm text-neutral-light duration-0 data-[state=active]:bg-transparent data-[state=active]:text-neutral-white sm:px-4 sm:py-4 sm:text-base"
                        >
                            <span>{trigger.text}</span>
                            <div className="absolute inset-0 top-auto h-1 w-full bg-white/20">
                                <motion.div
                                    className="h-full bg-white"
                                    initial={{ width: "0%" }}
                                    animate={{ width: activeTab === trigger.value ? "100%" : "0%" }}
                                    transition={{
                                        duration: activeTab === trigger.value ? 10 : 0.3,
                                        ease: "easeInOut",
                                    }}
                                />
                            </div>
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>
        </div>
    );
};

const TabContent = ({ heading, description, buttons, image }) => (
    <div className="flex h-[80vh] flex-col items-center justify-center">
        <div className="px-[5%] py-16 md:py-24 lg:py-28">
            <motion.div
                className="mx-auto max-w-lg text-center"
                initial={{ y: "20%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "-20%", opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
            >
                <h1
                    className="mb-5 text-6xl font-bold text-text-alternative md:mb-6 md:text-9xl lg:text-10xl"
                    dangerouslySetInnerHTML={{ __html: heading }}
                />
                <p className="text-text-alternative md:text-md">{description}</p>
                {buttons?.length > 0 && (
                    <div className="h-carousel-buttons d-flex justify-content-center">
                        {buttons.map((button, index) => (
                            <a key={index} href={button.href}>
                                <Button {...button}>{button.title}</Button>
                            </a>
                        ))}
                    </div>
                )}
            </motion.div>
        </div>
        <div className="absolute inset-0 -z-10">
            <div className="absolute inset-0 z-10 bg-black/50" />
            <motion.div
                className="absolute inset-0 bg-black"
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                exit={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            />
            <motion.img
                loading="lazy"
                className="size-full object-cover"
                src={image.src}
                alt={image.alt}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
            />
        </div>
    </div>
);

export default HomeBanner;
